'use client';

import { Button } from '@/components/ui/button/Button';

interface FullScreenErrorProps {
  /** Short heading, e.g. "We couldn't load your account". */
  title: string;
  /** Supporting line explaining what to do next. */
  message: string;
  /** Retry action label, e.g. "Try again". */
  retryLabel: string;
  /** Invoked when the user asks to retry (e.g. re-run the profile fetch). */
  onRetry: () => void;
}

/**
 * Full-screen boot error. Replaces the full-screen loader when app bootstrap
 * finishes but cannot render the account (e.g. the profile fetch failed), so the
 * user gets a bounded, recoverable state instead of an indefinite loader. (PB-3288)
 */
export function FullScreenError({ title, message, retryLabel, onRetry }: FullScreenErrorProps) {
  return (
    <div
      role="alert"
      data-cy="full-screen-error"
      className="tw:flex tw:h-dvh tw:w-full tw:flex-col tw:items-center tw:justify-center tw:gap-base tw:bg-gray-white tw:px-large tw:text-center"
    >
      <div className="tw:text-16 tw:font-semibold tw:text-font" data-cy="full-screen-error-title">
        {title}
      </div>
      <div className="tw:text-14 tw:text-gray-dark" data-cy="full-screen-error-message">
        {message}
      </div>
      <Button onClick={onRetry} data-cy="full-screen-error-retry">
        {retryLabel}
      </Button>
    </div>
  );
}
